import { Alert, Badge, Button, Group, Modal, Select, Stack, Text, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconClock, IconInfoCircle, IconX } from "@tabler/icons-react";
import { useState } from "react";

import { z } from "zod";
import { usePatchBoardsTemplateActionMutation } from "../../../../../openapi";
import { useAppSelector } from "../../../../hooks";
import { IssueBoardTemplateStatus, ZTemplateAction } from "../../../../mc-shared/enums";
import { selectHttpIssueBoard } from "../redux/selectors/issueRtkSelectors";

type TemplateStatusBannerProps = {
  isAdminOrCanApprove?: boolean;
};

const TEMPLATE_ACTIONS_OPTIONS = [
  { value: "SET_TO_REVIEW", label: "Send til gjennomgang" },
  { value: "APPROVED", label: "Godkjenn mal" },
  { value: "REJECTED", label: "Avvis / sett tilbake til opprettet" },
  { value: "UPDATED", label: "Oppdatert (sett til gjennomgang)" },
] as const;

const getStatusColor = (status?: string) => {
  switch (status) {
    case "APPROVED":
      return "green";
    case "UNDER_REVIEW":
      return "blue";
    case "CREATED":
    default:
      return "gray";
  }
};

const getStatusLabel = (status?: string) => {
  switch (status) {
    case "APPROVED":
      return "Godkjent";
    case "UNDER_REVIEW":
      return "Under gjennomgang";
    case "CREATED":
      return "Utkast / ikke godkjent";
    default:
      return status || "Ukjent";
  }
};

const IssueBoardMcTemplateBanner: React.FC<TemplateStatusBannerProps> = ({ isAdminOrCanApprove = true }) => {
  const issueBoard = useAppSelector(selectHttpIssueBoard);

  const [opened, setOpened] = useState(false);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [comment, setComment] = useState("");

  const [patchAction, { isLoading: isPatching }] = usePatchBoardsTemplateActionMutation();

  const currentStatus = issueBoard.template.status || "CREATED";
  const approvedBy = issueBoard.template.approvedBy;
  const approvalDate = issueBoard.template.approvalDate
    ? new Date(issueBoard.template.approvalDate).toLocaleDateString("nb-NO")
    : null;

  const closeModal = () => {
    setOpened(false);
    setComment("");
    setSelectedAction(null);
  };

  const handleSubmit = async () => {
    if (!selectedAction) return;

    try {
      await patchAction({
        body: {
          boardId: issueBoard._id,
          action: selectedAction as z.infer<typeof ZTemplateAction>,
          comment: comment.trim(),
        },
      }).unwrap();

      notifications.show({
        title: "Status oppdatert",
        message: `Malen er nå satt til ${getStatusLabel(
          IssueBoardTemplateStatus[selectedAction as keyof typeof IssueBoardTemplateStatus],
        )}`,
        color: "green",
        icon: <IconCheck size={16} />,
      });

      closeModal();
    } catch (err: any) {
      notifications.show({
        title: "Feil",
        message: err?.data?.message || "Kunne ikke oppdatere status",
        color: "red",
        icon: <IconX size={16} />,
      });
    }
  };

  const isApproved = currentStatus === "APPROVED";

  return (
    <>
      <Alert
        color={getStatusColor(currentStatus)}
        variant="light"
        bg={isApproved ? "green.2" : "yellow.2"}
        radius={0}
        p="sm"
        m="xs"
      >
        <Group justify="space-between" wrap="wrap">
          <Group gap="sm">
            {isApproved ? <IconCheck size={20} /> : <IconClock size={20} />}
            <Text fw={600} size="sm">
              Malstatus:
            </Text>
            <Badge size="sm" color={getStatusColor(currentStatus)} variant="filled">
              {getStatusLabel(currentStatus)}
            </Badge>
            {isApproved && approvedBy && (
              <Text size="sm" c="dimmed">
                Godkjent av <strong>{approvedBy.name}</strong> den {approvalDate}
              </Text>
            )}
            {!isApproved && (
              <Text size="sm" c="dimmed">
                Denne malen er ikke godkjent enda.
              </Text>
            )}
          </Group>
          {isAdminOrCanApprove && (
            <Button
              variant="outline"
              size="sm"
              color={isApproved ? "gray" : "blue"}
              onClick={() => setOpened(true)}
              disabled={isPatching}
            >
              Endre status
            </Button>
          )}
        </Group>
      </Alert>

      <Modal opened={opened} onClose={closeModal} title={<Title order={3}>Endre malstatus</Title>} centered size="md">
        <Stack>
          <Select
            label="Velg handling"
            placeholder="Velg ny status / handling"
            data={TEMPLATE_ACTIONS_OPTIONS}
            value={selectedAction}
            onChange={setSelectedAction}
            required
          />

          {selectedAction === "APPROVED" && (
            <Alert color="yellow" icon={<IconInfoCircle size={18} />}>
              Når du godkjenner denne malen blir den tilgjengelig for alle i Multiconsult. Er du sikker?
            </Alert>
          )}

          {(selectedAction === "REJECTED" || selectedAction === "APPROVED") && (
            <TextInput
              label="Kommentar (valgfri)"
              placeholder="Grunn for endring..."
              value={comment}
              onChange={(e) => setComment(e.currentTarget.value)}
            />
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeModal}>
              Avbryt
            </Button>
            <Button
              loading={isPatching}
              disabled={!selectedAction}
              color={selectedAction === "APPROVED" ? "green" : "blue"}
              onClick={handleSubmit}
            >
              Bekreft endring
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default IssueBoardMcTemplateBanner;
